import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router';
import RoleGuard from '../../../authHelpers/RoleGuard';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { useAuth } from '../../../context/AuthContext';
import { MODERATOR_QUEUE } from '../../../graphql/resolvers/moderation/moderatorQueue.gql';
import Denied from '../Denied';
import ReportModal from '../ReportModal';
import { groupReports, type ModeratorQueueEntry } from './groupReports';
import QueueTabs, { type Tab } from './QueueTabs';
import ReportFilters from './ReportFilters';
import ReportGroupRow from './ReportGroupRow';
import ReportRowSkeleton from './ReportRowSkeleton';
import ViewToggle, { useViewMode } from './ViewToggle';
import '../moderation.scss';

const POLL_INTERVAL_MS = 300000;

const CATEGORY_ALLOWLIST = new Set(['SAFETY', 'INTEGRITY', 'LEGAL', 'QUALITY']);
const REASON_ALLOWLIST = new Set([
  'HARASSMENT',
  'HATE_SPEECH',
  'SEXUAL_CONTENT',
  'SELF_HARM',
  'CHILD_SAFETY',
  'UNLABELED_AI',
  'IMPERSONATION',
  'SCAM_FRAUD',
  'SPAM',
  'IP_THEFT',
  'UNDISCLOSED_ADS',
  'REGULATED_GOODS',
  'GRAPHIC_CONTENT',
  'OFF_TOPIC',
  'SPOILERS',
]);

function parseTab(raw: string | null): Tab {
  if (raw === 'ALL' || raw === 'NEEDS_ADMIN' || raw === 'RESOLVED') return raw;
  return 'MINE';
}

function InnerPage() {
  const { user } = useAuth();
  // Guarded by RoleGuard so user is hydrated and has role ≥ moderator here.
  const role = (user as { role?: string } | null)?.role;
  const isAdmin = role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useViewMode();

  // Non-admin moderators only have access to the MINE tab — coerce deep-linked ALL/NEEDS_ADMIN
  // URLs so we don't fire a query the server will reject with a generic error.
  const requestedTab = parseTab(searchParams.get('tab'));
  const tab: Tab = isAdmin ? requestedTab : 'MINE';
  const postId = searchParams.get('post');
  // Reject unknown enum values from stale/malformed URLs rather than let GraphQL variable
  // coercion fail the whole query. An empty list is treated as "no filter" downstream.
  const categoryFilterRaw = (searchParams.get('category') ?? '')
    .split(',')
    .filter((v) => v && CATEGORY_ALLOWLIST.has(v));
  const reasonFilterRaw = (searchParams.get('reason') ?? '')
    .split(',')
    .filter((v) => v && REASON_ALLOWLIST.has(v));
  const assigneeFilter = searchParams.get('assignee') || null;
  // Cursor from URL — minimum viable pagination so the empty-state isn't a
  // false signal when the server caps its scan at MAX_SCAN_PAGES. Full
  // fetchMore + cross-page grouping is TRIL-65.
  //
  // Cursor is only honored when the raw URL's intended tab matches the tab
  // we're about to query. This catches three unsafe URL shapes:
  //   1. Non-admin coerced from an admin-only tab — rawTab=ALL, tab=MINE
  //   2. Malformed tab values (parseTab silently maps 'bogus' → MINE, but the
  //      cursor in that URL might have been written by a different query path)
  //   3. Anything that breaks the invariant "cursor was written by the same
  //      access path we're about to replay"
  // Exception: when rawTab is absent, we treat the URL as a default MINE view,
  // since our load-more handler writes cursor without always writing tab.
  const rawTab = searchParams.get('tab');
  const RAW_TAB_KNOWN: ReadonlySet<string> = new Set([
    'MINE',
    'ALL',
    'NEEDS_ADMIN',
    'RESOLVED',
  ]);
  const rawTabIsKnown = rawTab === null || RAW_TAB_KNOWN.has(rawTab);
  const rawTabEffective = (rawTab ?? 'MINE') as Tab;
  const cursor =
    rawTabIsKnown && rawTabEffective === tab
      ? searchParams.get('cursor') || null
      : null;

  const { data, loading, error, refetch } = useQuery(MODERATOR_QUEUE, {
    variables: {
      tab,
      categoryFilter: categoryFilterRaw.length > 0 ? categoryFilterRaw : null,
      reasonFilter: reasonFilterRaw.length > 0 ? reasonFilterRaw : null,
      assigneeFilter,
      limit: 50,
      cursor,
    },
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const entries = (data?.moderatorQueue?.entries ??
    []) as ModeratorQueueEntry[];
  const groups = groupReports(entries);

  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    // Cursor is scoped to the current tab's scan — a MINE cursor is a GSI key,
    // ALL/NEEDS_ADMIN cursors are table PK/SK. Never carry a cursor across
    // tabs; DynamoDB would reject it or paginate from the wrong position.
    nextParams.delete('cursor');
    setSearchParams(nextParams);
  };

  const openReportModal = (pid: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('post', pid);
    setSearchParams(nextParams);
  };

  const closeReportModal = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('post');
    setSearchParams(nextParams);
  };

  const counts = {
    MINE: tab === 'MINE' ? entries.length : 0,
    ALL: tab === 'ALL' ? entries.length : 0,
    NEEDS_ADMIN: tab === 'NEEDS_ADMIN' ? entries.length : 0,
    RESOLVED: tab === 'RESOLVED' ? entries.length : 0,
  };

  return (
    <div className="Moderation">
      <header className="Moderation__Header">
        <h1>Moderation</h1>
        <div className="Moderation__Header__Actions">
          <ViewToggle mode={viewMode} onChange={setViewMode} />
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={() => refetch()}
          >
            Refresh
          </ButtonComponent>
        </div>
      </header>

      <QueueTabs
        tab={tab}
        isAdmin={isAdmin}
        counts={counts}
        onChange={setTab}
      />

      <ReportFilters
        isAdmin={isAdmin}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
      />

      {loading && entries.length === 0 && <ReportRowSkeleton count={5} />}

      {error && !loading && (
        <div className="Moderation__Error">
          Unable to load the queue.{' '}
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={() => refetch()}
          >
            Retry
          </ButtonComponent>
        </div>
      )}

      {groups.length > 0 && (
        <div className="Moderation__List">
          {groups.map((g) => (
            <ReportGroupRow
              key={g.postId}
              group={g}
              variant={viewMode}
              onOpen={openReportModal}
            />
          ))}
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div className="Moderation__Empty">
          {data?.moderatorQueue?.cursor ? (
            // Server capped its scan (MAX_SCAN_PAGES) and returned an empty
            // page with a cursor. Show an explanation; the "Continue scanning"
            // control below handles the action.
            <p>No matches on this page yet — the server is still scanning.</p>
          ) : (
            <>
              {tab === 'MINE' && 'No reports assigned to you right now.'}
              {tab === 'ALL' && 'No pending reports across the platform.'}
              {tab === 'NEEDS_ADMIN' &&
                'No reports in the admin queue — all incoming reports found a moderator.'}
              {tab === 'RESOLVED' && 'No resolved reports found.'}
            </>
          )}
        </div>
      )}

      {/* Load-more is independent of the empty state. A non-null cursor can
          accompany ANY page — including a capped page with partial matches
          (e.g. 3 rows + cursor). Rendering the control only inside the empty
          branch would hide the rest of the queue behind those 3 rows. */}
      {!loading && !error && data?.moderatorQueue?.cursor && (
        <div className="Moderation__LoadMore">
          <button
            type="button"
            className="Moderation__Empty__LoadMore"
            data-testid="moderation-load-more"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              // Normalize the tab when writing the next cursor. Without this,
              // a URL like ?tab=bogus reaches the click handler, only `cursor`
              // is set, and the cursor guard on the next render rejects the
              // fresh cursor (rawTab 'bogus' isn't in the known set) — the
              // user clicks Continue and nothing advances.
              next.set('tab', tab);
              next.set('cursor', data.moderatorQueue.cursor as string);
              setSearchParams(next);
            }}
          >
            Continue scanning
          </button>
        </div>
      )}

      <ReportModal postId={postId} onClose={closeReportModal} />
    </div>
  );
}

export default function ModerationQueuePage() {
  return (
    <RoleGuard minRole="moderator" fallback={<Denied />}>
      <InnerPage />
    </RoleGuard>
  );
}
